import {
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconCalendarMonth,
  IconCalendarTime,
  IconFileTypeCsv,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { BASE_API_DELIVERY } from "../../../config";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { formatDateNotTimeZone } from "../../../common/FormatDate/FormatDate";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

// Các cột (từ "SL BH" trở về sau) có hiệu ứng hover đổi màu + tooltip "Xem chi tiết"
const HOVER_DETAIL_COLUMNS = [
  "sl_bh",
  "sl_bh_ban_truoc",
  "sl_bh_ban_trong",
  "sl_bh_hang_kho",
  "sl_gui_bh_lai",
  "sl_ban_trong",
  "sl_ban_theo_ky",
  "ti_le_hang_kho_loi",
  "ti_le_ban_ra_bh",
  "ti_le_bh_thang",
  "ti_le_loi_3_thang",
  "ti_le_loi_lai",
];

const ReportDefectiveGoodsRate = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    thang:
      _searchJson && checkedPinSearch
        ? _searchJson?.thang
        : String(new Date().getMonth() + 1),
  });
  const [nam, setNam] = useDebouncedState<string>(
    _searchJson && checkedPinSearch
      ? _searchJson?.nam
      : String(new Date().getFullYear()),
    300,
  );
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const [monthViewTable, setMonthViewTable] = useState<string>("");
  const [yearViewTable, setYearViewTable] = useState<string>("");

  const dataMonth = [
    { label: "Tháng 1", value: "1" },
    { label: "Tháng 2", value: "2" },
    { label: "Tháng 3", value: "3" },
    { label: "Tháng 4", value: "4" },
    { label: "Tháng 5", value: "5" },
    { label: "Tháng 6", value: "6" },
    { label: "Tháng 7", value: "7" },
    { label: "Tháng 8", value: "8" },
    { label: "Tháng 9", value: "9" },
    { label: "Tháng 10", value: "10" },
    { label: "Tháng 11", value: "11" },
    { label: "Tháng 12", value: "12" },
  ];

  // Dựng link sang màn Xuất bán / Nhập lại kèm bộ lọc theo tháng năm đang xem
  // columnType = "sl_ban_trong": lấy đúng tháng đang xem (ngày đầu -> cuối tháng)
  // columnType = "sl_ban_theo_ky": lấy kỳ 3 tháng (từ tháng monthViewTable - 2 -> monthViewTable)
  const buildExportUrl = (
    row: any,
    columnType: "sl_ban_trong" | "sl_ban_theo_ky",
  ): string | undefined => {
    const year = Number(yearViewTable);
    const month = Number(monthViewTable);

    if (!year || !month) return undefined;

    const startMonth = columnType === "sl_ban_theo_ky" ? month - 2 : month;

    // Ngày đầu của tháng bắt đầu
    const startDate = new Date(year, startMonth - 1, 1, 0, 0, 0);
    // Ngày cuối của tháng monthViewTable
    const endDate = new Date(year, month, 0, 23, 59, 59);

    const params = new URLSearchParams();
    params.set("startDate", formatDateNotTimeZone(startDate));
    params.set("endDate", formatDateNotTimeZone(endDate));
    if (row?.original?.ma_nhom) {
      params.set("MaSp", String(row.original.ma_nhom));
    }

    return `/warranty/export-or-re-export?${params.toString()}`;
  };

  // Ô số liệu render bằng thẻ <a> thật để Ctrl/Cmd + click hoặc click chuột giữa
  // mở được tab nền theo cơ chế native của trình duyệt.
  // Click thường không điều hướng (tránh rời trang ngoài ý muốn), double-click mở tab mới.
  const renderExportLinkCell = (
    renderedCellValue: React.ReactNode,
    row: any,
    columnType: "sl_ban_trong" | "sl_ban_theo_ky",
  ) => {
    const url = buildExportUrl(row, columnType);

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          // Có phím bổ trợ -> nhường trình duyệt xử lý, tuyệt đối không preventDefault
          // vì sẽ chặn luôn cả việc mở tab nền.
          if (e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.preventDefault();
        }}
        onDoubleClick={() =>
          url && window.open(url, "_blank", "noopener,noreferrer")
        }
        style={{
          color: "inherit",
          textDecoration: "none",
          cursor: "pointer",
          display: "block",
        }}
      >
        <Text fw={500} size="12.5px">
          {renderedCellValue}
        </Text>
      </a>
    );
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "ma_nhom",
        header: "Mã sản phẩm",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "ten_nhom",
        header: "Tên sản phẩm",
        enableColumnFilter: false,
        enableSorting: false,
        size: 400,
      },
      {
        accessorKey: "sl_bh",
        header: `SL BH tháng ${monthViewTable}/${yearViewTable}`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "sl_bh_ban_truoc",
        header: `SL BH bán trước tháng ${monthViewTable}/${yearViewTable}`,
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "sl_bh_ban_trong",
        header: `SL BH bán ra trong tháng ${monthViewTable}/${yearViewTable}`,
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "sl_bh_hang_kho",
        header: `SL BH Hàng kho trong tháng ${monthViewTable}/${yearViewTable}`,
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "sl_gui_bh_lai",
        header: `SL gửi BH lại tháng ${monthViewTable}/${yearViewTable}`,
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "sl_ban_trong",
        header: `SL bán trong tháng ${monthViewTable}/${yearViewTable}`,
        Cell: ({ renderedCellValue, row }) =>
          renderExportLinkCell(renderedCellValue, row, "sl_ban_trong"),
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "sl_ban_theo_ky",
        header: "SL bán kỳ 3 tháng",
        Cell: ({ renderedCellValue, row }) =>
          renderExportLinkCell(renderedCellValue, row, "sl_ban_theo_ky"),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "ti_le_hang_kho_loi",
        header: `Tỉ lệ Hàng kho lỗi tháng ${monthViewTable}/${yearViewTable}`,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || String(renderedCellValue) === "0" ? (
            <Text size="12.5px" fw={500}>
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "ti_le_ban_ra_bh",
        header: `Tỉ lệ bán ra BH tháng ${monthViewTable}/${yearViewTable}`,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || String(renderedCellValue) === "0" ? (
            <Text size="12.5px" fw={500}>
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "ti_le_bh_thang",
        header: `Tỷ lệ BH trong tháng ${monthViewTable}/${yearViewTable}`,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || String(renderedCellValue) === "0" ? (
            <Text size="12.5px" fw={500}>
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "ti_le_loi_3_thang",
        header: "Tỷ lệ kỳ 3 tháng",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || String(renderedCellValue) === "0" ? (
            <Text size="12.5px" fw={500}>
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "ti_le_loi_lai",
        header: "Tỷ lệ lỗi lại",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue || String(renderedCellValue) === "0" ? (
            <Text size="12.5px" fw={500}>
              {renderedCellValue} %
            </Text>
          ) : (
            <></>
          ),
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
      },
    ],
    [monthViewTable, yearViewTable],
  );

  const validateSearch = (): boolean => {
    // Kiểm tra tháng tồn tại và độ dài khác 0
    if (!search.thang || String(search.thang).length === 0) {
      NotificationExtension.Fails("Vui lòng chọn tháng !");
      return false;
    }

    // Kiểm tra năm tồn tại và độ dài khác 0
    if (!nam || String(nam).length === 0) {
      NotificationExtension.Fails("Vui lòng nhập năm !");
      return false;
    }

    const thang = Number(search.thang);
    const namNumber = Number(nam);

    // Kiểm tra tháng hợp lệ
    if (isNaN(thang) || thang < 1 || thang > 12) {
      NotificationExtension.Fails("Tháng không hợp lệ !");
      return false;
    }

    // Kiểm tra năm hợp lệ
    if (isNaN(namNumber) || namNumber <= 0) {
      NotificationExtension.Fails("Năm không hợp lệ !");
      return false;
    }

    // Kiểm tra tháng năm không được lớn hơn tháng năm hiện tại
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (
      namNumber > currentYear ||
      (namNumber === currentYear && thang > currentMonth)
    ) {
      NotificationExtension.Fails(
        "Tháng năm không được lớn hơn tháng năm hiện tại !",
      );
      return false;
    }

    return true;
  };

  const fetchData = async () => {
    if (!validateSearch()) return;

    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    setMonthViewTable(search.thang);
    setYearViewTable(nam);
    try {
      let url = `/api/v1/DefectiveItemsList/get-list-warranty-defect-rate-report?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (nam.length > 0) {
        url += `&nam=${nam}`;
      }

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params?.toString()}`;

      const res = await repositoryDelivery.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
          nam: nam,
        }),
      );
    }
  };

  const exportDataExcel = async () => {
    if (!validateSearch()) return;

    open();
    try {
      let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (nam.length > 0) {
        url += `&nam=${nam}`;
      }

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const baseUrl = `/api/v1/ExportExcel/export-warranty-defect-rate-report${url}`;

      const _url = BASE_API_DELIVERY + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "bao_cao_ty_le_hang_loi_bao_hanh.xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        NotificationExtension.Success("Export Excel thành công !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningReportDefectiveGoodsRate",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers", "ma_nhom"],
          right: ["action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningReportDefectiveGoodsRate",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (150 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ column }) => {
      const isDetailCell = HOVER_DETAIL_COLUMNS.includes(column.id);
      return {
        title: isDetailCell ? "Nháy đúp để xem chi tiết" : undefined,
        onMouseEnter: isDetailCell
          ? (e: React.MouseEvent<HTMLTableCellElement>) => {
              e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.07)";
            }
          : undefined,
        onMouseLeave: isDetailCell
          ? (e: React.MouseEvent<HTMLTableCellElement>) => {
              e.currentTarget.style.backgroundColor = "";
            }
          : undefined,
        style: {
          fontWeight: "500", //conditional styling
          fontSize: "12.5px",
          padding: "5px 15px",
          cursor: isDetailCell ? "pointer" : undefined,
          transition: "background-color 0.15s ease",
        },
      };
    },
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
      highlightOnHover: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconFileTypeCsv size={16} />}
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Tháng"
              leftSection={<IconCalendarMonth color="#15aabf" />}
              data={dataMonth}
              value={search.thang || ""}
              searchable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              onChange={(e) => {
                changeSearchValue(e ? e : "", "thang", setSearch);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Năm"
              leftSection={<IconCalendarTime color="#15aabf" />}
              type="number"
              defaultValue={nam}
              onChange={(e) => setNam(e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ReportDefectiveGoodsRate;
